import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SectorStore } from '../stores/sector.store';

export interface User {
  id: string;
  username: string;
  fullname: string;
  sectorName: string;
  sectorId: number;
  roleId: number;
  roleName: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

// Estructura de respuesta del backend
export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
  code: number;
  timestamp: string;
}

export interface LoginData {
  // El token ahora viene en httpOnly cookie - no en el response body
  userId: string;
  fullName: string;
  sectorName: string;
  roleName: string;
  sectorId: number;
  username: string;
  roleId: number;
}

/**
 * Servicio de autenticación JWT para la aplicación.
 *
 * 🔒 RESUMEN DE SEGURIDAD:
 * ✅ Implementado:
 *    - Refresh automático de tokens en 401
 *    - Validación de expiración antes de navegación
 *    - Guards de autenticación en rutas
 *    - HTTPS en producción
 *
 * ⚠️ Vulnerabilidades conocidas:
 *    - Tokens en localStorage (vulnerable a XSS)
 *    - Sin patrón de refresh token separado
 *    - Sin revocación de tokens en backend
 *    - Sin verificación de firma en cliente (solo backend)
 *
 * 📋 Mejoras recomendadas (ver documentación completa):
 *    1. Migrar a httpOnly cookies
 *    2. Implementar refresh tokens separados
 *    3. Agregar revocación de tokens (blacklist)
 *    4. Implementar CSP estricto
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = environment.apiUrl;
  private readonly USER_KEY = 'user_data';

  // Signals para estado reactivo
  private currentUserSignal = signal<User | null>(this.getStoredUser());
  
  // Computed signals
  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.currentUserSignal());
  readonly userRole = computed(() => this.currentUserSignal()?.roleId ?? 0);

  private sectorStore = inject(SectorStore);
  private http = inject(HttpClient);
  private router = inject(Router);

  login(credentials: LoginRequest): Observable<ApiResponse<LoginData>> {
    return this.http.post<ApiResponse<LoginData>>(`${this.API_URL}/auth/admin/login`, credentials).pipe(
      tap(response => {
        this.setSession(response.data);
        const user: User = {
          id: response.data.userId,
          fullname: response.data.fullName,
          username: response.data.username,
          sectorId: response.data.sectorId,
          sectorName: response.data.sectorName,
          roleId: response.data.roleId,
          roleName: response.data.roleName
        };
        this.currentUserSignal.set(user);
      }),
      catchError(error => {
        console.error('Error en login:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Cierra la sesión del usuario actual.
   *
   * NOTA DE SEGURIDAD - REVOCACIÓN DE TOKENS:
   * - Actualmente solo limpia el localStorage del cliente
   * - El token sigue siendo VÁLIDO en el backend hasta su expiración
   * - RIESGO: Si el token fue robado, el atacante puede seguir usándolo
   *
   * MEJORA RECOMENDADA:
   * - Implementar endpoint /auth/logout en el backend
   * - Backend debe agregar token a una blacklist (Redis recomendado)
   * - Validar tokens contra blacklist en cada request
   * - Invalidar TODOS los tokens del usuario al cambiar contraseña
   */
  logout(): void {
    this.clearSession();
    this.currentUserSignal.set(null);
    // Navegar antes de resetear el store para no bloquear la navegación
    // con los cambios reactivos del store (4 signals cambian en reset())
    this.router.navigate(['/login'], { replaceUrl: true });
    // Resetear el store después de iniciar la navegación
    this.sectorStore.reset();
  }

  /**
   * Refresca el token JWT llamando al endpoint de refresh.
   *
   * ✅ SEGURIDAD CON httpOnly COOKIES:
   * - El refreshToken también viene en httpOnly cookie
   * - El navegador lo envía automáticamente con withCredentials: true
   * - No necesitamos enviar el token en el body
   * - El backend maneja la rotación de tokens de forma segura
   */
  refreshToken(): Observable<ApiResponse<LoginData>> {
    // No necesitamos enviar el token - viene en la cookie httpOnly
    return this.http.post<ApiResponse<LoginData>>(`${this.API_URL}/auth/refresh`, {}).pipe(
      tap(response => {
        // Actualizar datos del usuario si vienen en la respuesta
        if (response.data) {
          this.setSession(response.data);
        }
      }),
      catchError(error => {
        // Si el refresh falla, la sesión está comprometida - logout automático
        this.logout();
        return throwError(() => error);
      })
    );
  }

  /**
   * ✅ SEGURIDAD: Tokens ahora manejados por httpOnly cookies
   * - El navegador envía automáticamente la cookie en cada request
   * - No necesitamos obtener el token manualmente
   * - Retornamos null porque el interceptor ya no necesita inyectar el header
   */
  getToken(): string | null {
    // Ya no usamos localStorage para tokens - vienen en httpOnly cookies
    return null;
  }

  getRefreshToken(): string | null {
    return null;
  }

  /**
   * Verifica si hay una sesión válida.
   *
   * ✅ SEGURIDAD CON httpOnly COOKIES:
   * - Ya no podemos decodificar el token (está en httpOnly cookie)
   * - El backend valida el token en cada request
   * - Verificamos si tenemos datos de usuario almacenados
   * - Si el backend devuelve 401, el interceptor manejará el logout automático
   *
   * @returns true si no hay sesión activa, false si hay usuario logueado
   */
  isTokenExpired(): boolean {
    // Si no hay usuario en memoria, asumimos que no hay sesión
    return !this.currentUserSignal();
  }

  /**
   * Almacena los datos del usuario.
   *
   * ✅ SEGURIDAD MEJORADA:
   * - El token JWT ahora se maneja mediante httpOnly cookies desde el backend
   * - Las cookies httpOnly NO son accesibles desde JavaScript (protección contra XSS)
   * - El navegador envía automáticamente las cookies en cada request
   * - Solo almacenamos datos NO sensibles del usuario en localStorage para UX
   */
  private setSession(data: LoginData): void {
    // El token viene en httpOnly cookie - NO lo guardamos en localStorage
    // Solo guardamos información del usuario para mostrar en la UI
    const user: User = {
      id: data.userId,
      username: data.username,
      sectorId: data.sectorId,
      roleId: data.roleId,
      fullname: data.fullName,
      sectorName: data.sectorName,
      roleName: data.roleName
    };
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));

    // Actualizar el signal para UI reactiva
    this.currentUserSignal.set(user);
  }

  private clearSession(): void {
    // Solo limpiar datos de usuario - el token está en httpOnly cookie
    // El backend debe limpiar la cookie en el endpoint /auth/logout
    localStorage.removeItem(this.USER_KEY);
  }

  private getStoredUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }
}
